import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ChevronDown, Download, FileClock, FileDown, FileSpreadsheet, History, Loader2, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DataTable,
  type Column,
} from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { SearchInput } from "@/components/data/search-input";
import {
  RegistrationStatusBadge,
  REGISTRATION_TONE,
  TONE_CLASSES,
  toneDotClass,
} from "@/components/data/status-badge";
import { RefreshButton } from "@/components/data/refresh-button";
import {
  DateRangeFilter,
  dayToIso,
} from "@/components/data/date-range-filter";
import {
  registrationsApi,
  downloadLayoutTemplate,
  type Registration,
  REGISTRATION_STATUSES,
  type RegistrationStatus,
} from "@/api/registrations";
import { queryKeys } from "@/api/query-keys";
import { useCursorList } from "@/hooks/use-cursor-list";
import { authErrorMessage } from "@/auth/errors";
import { requireRole } from "@/auth/role-guard";
import { formatDateTime, formatRelative } from "@/lib/format";
import { UploadLayoutDialog } from "./-dialogs/upload-layout-dialog";
import { ExportsDock } from "./-components/exports-dock";
import { LayoutUploadsDock } from "./-components/layout-uploads-dock";
import {
  RugLoginGuardBanner,
  useRugLoginValid,
} from "@/components/rug/rug-login-guard";


const searchSchema = z.object({
  status: z.string().optional(),
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/registrations/")({
  validateSearch: searchSchema,
  beforeLoad: requireRole(["admin", "inscripcion"]),
  component: RegistrationsPage,
});

function RegistrationsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const navigateTo = useNavigate();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(search.q ?? "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      await downloadLayoutTemplate();
    } catch (err) {
      toast.error(authErrorMessage(err));
    } finally {
      setDownloadingTemplate(false);
    }
  };
  const { isValid: loginValid } = useRugLoginValid("inscripcion");

  // Multi-estado: en la URL viaja como CSV ("queued,signed_failed"). Vacío = todos.
  const selectedStatuses = (
    search.status ? search.status.split(",").filter(Boolean) : []
  ) as RegistrationStatus[];
  const statusParam = selectedStatuses.length ? selectedStatuses.join(",") : undefined;

  // Listado paginado por cursor — se usa cuando no hay búsqueda activa
  const listParams = {
    status: statusParam,
    from: dayToIso(search.from, "start"),
    to: dayToIso(search.to, "end"),
    limit: 25,
  };
  const listQuery = useCursorList({
    queryKey: queryKeys.registrations.list(listParams),
    fetcher: registrationsApi.list,
    params: listParams,
    enabled: !searchTerm,
  });

  // Búsqueda — un solo cuadro, texto libre (LIKE sobre contrato + nombre + RFC en
  // el backend) y RESPETANDO los filtros de estado/fecha activos.
  const searchParams = {
    q: searchTerm.trim(),
    status: statusParam,
    from: dayToIso(search.from, "start"),
    to: dayToIso(search.to, "end"),
  };
  const searchQuery = useQuery({
    queryKey: queryKeys.registrations.search(searchParams),
    queryFn: () => registrationsApi.search(searchParams),
    enabled: !!searchTerm,
  });

  // Contadores GLOBALES (no reaccionan a fecha/búsqueda). Clic en un chip filtra.
  const countsQuery = useQuery({
    queryKey: queryKeys.registrations.counts(),
    queryFn: registrationsApi.counts,
  });
  const counts = countsQuery.data;

  // Export a Excel: respeta el filtro activo (estado/fecha/búsqueda); vacío = todas.
  const [exportsOpen, setExportsOpen] = useState(false);
  const [uploadsHistoryOpen, setUploadsHistoryOpen] = useState(false);
  const exportMutation = useMutation({
    mutationFn: () =>
      registrationsApi.createExport({
        filter: {
          q: searchTerm.trim() || undefined,
          statuses: selectedStatuses.length ? selectedStatuses : undefined,
          from: dayToIso(search.from, "start"),
          to: dayToIso(search.to, "end"),
        },
      }),
    onSuccess: () => {
      toast.success(t("registrations.exports.startedToast"));
      setExportsOpen(true);
      qc.invalidateQueries({ queryKey: queryKeys.registrations.exports() });
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => registrationsApi.retry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      toast.success(t("registrations.retryToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const onSearchChange = (value: string) => {
    setSearchTerm(value);
    navigate({
      search: (prev) => ({ ...prev, q: value || undefined }),
      replace: true,
    });
  };

  const toggleStatus = (value: RegistrationStatus) => {
    const next = selectedStatuses.includes(value)
      ? selectedStatuses.filter((s) => s !== value)
      : [...selectedStatuses, value];
    navigate({
      search: (prev) => ({ ...prev, status: next.length ? next.join(",") : undefined }),
      replace: true,
    });
  };
  const clearStatuses = () => {
    navigate({
      search: (prev) => ({ ...prev, status: undefined }),
      replace: true,
    });
  };

  const columns: Column<Registration>[] = [
    {
      id: "contract",
      header: t("registrations.columns.contract"),
      cell: (r) => (
        <div>
          <p className="font-medium">{r.contractNumber}</p>
          {r.folioRug ? (
            <p className="text-xs text-muted-foreground">
              {t("registrations.columns.folio", { folio: r.folioRug })}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: "grantor",
      header: t("registrations.columns.grantor"),
      cell: (r) => (
        <div>
          <p className="text-sm">{r.grantorName}</p>
          <p className="text-xs text-muted-foreground font-mono">
            {r.grantorRfc}
          </p>
        </div>
      ),
    },
    {
      id: "creditor",
      header: t("registrations.columns.creditor"),
      cell: (r) => <p className="text-sm">{r.principalCreditor}</p>,
    },
    {
      id: "status",
      header: t("registrations.columns.status"),
      cell: (r) => <RegistrationStatusBadge status={r.status} />,
    },
    {
      id: "attempts",
      header: t("registrations.columns.attempts"),
      cell: (r) => (
        <span className="text-sm tabular-nums">{r.attempts}</span>
      ),
      headClassName: "text-right",
      className: "text-right",
    },
    {
      id: "created",
      header: t("registrations.columns.created"),
      cell: (r) => (
        <span className="text-sm text-muted-foreground">
          {formatDateTime(r.createdAt)}
        </span>
      ),
    },
    {
      id: "updated",
      header: t("registrations.columns.updated"),
      cell: (r) => (
        <span className="text-sm text-muted-foreground">
          {formatRelative(r.updatedAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (r) =>
        r.status === "signed_failed" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={retryMutation.isPending || !loginValid}
            onClick={(e) => {
              e.stopPropagation();
              retryMutation.mutate(r.id);
            }}
          >
            <RefreshCw className="size-3.5" />
            {t("registrations.actions.retry")}
          </Button>
        ) : null,
      headClassName: "w-32",
      className: "text-right",
    },
  ];

  const isSearching = !!searchTerm;
  const data = isSearching ? (searchQuery.data ?? []) : listQuery.items;
  const isLoading = isSearching ? searchQuery.isLoading : listQuery.isLoading;
  const isFetching = isSearching ? searchQuery.isFetching : listQuery.isFetching;
  const error = isSearching ? searchQuery.error : listQuery.error;

  return (
    <>
      <PageHeader
        title={t("registrations.title")}
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton
              onClick={() => {
                listQuery.refetch();
                countsQuery.refetch();
              }}
              spinning={listQuery.isFetching}
            />
            <Button
              variant="outline"
              onClick={() => exportMutation.mutate()}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <FileDown className="size-4" />
              )}
              {t("registrations.exports.export")}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setExportsOpen(true)}
              title={t("registrations.exports.history")}
            >
              <History className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setUploadsHistoryOpen(true)}
              title={t("registrations.layoutUploads.open")}
              aria-label={t("registrations.layoutUploads.open")}
            >
              <FileClock className="size-4" />
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
            >
              <Download className="size-4" />
              {t("registrations.upload.downloadTemplate")}
            </Button>
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="size-4" />
              {t("registrations.actions.uploadLayout")}
            </Button>
          </div>
        }
      />

      <div className="mb-4">
        <RugLoginGuardBanner flow="inscripcion" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <SearchInput
          value={searchTerm}
          onChange={onSearchChange}
          placeholder={t("registrations.search.placeholder")}
          className="sm:max-w-md flex-1"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="sm:w-48 justify-between font-normal">
              <span className="truncate">
                {selectedStatuses.length === 0
                  ? t("registrations.filters.status.all")
                  : selectedStatuses.length === 1
                    ? t(`registrations.filters.status.${selectedStatuses[0]}`)
                    : t("registrations.filters.statusCount", {
                        count: selectedStatuses.length,
                      })}
              </span>
              <ChevronDown className="size-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuCheckboxItem
              checked={selectedStatuses.length === 0}
              onCheckedChange={clearStatuses}
              onSelect={(e) => e.preventDefault()}
            >
              {t("registrations.filters.status.all")}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            {REGISTRATION_STATUSES.map((value) => (
              <DropdownMenuCheckboxItem
                key={value}
                checked={selectedStatuses.includes(value)}
                onCheckedChange={() => toggleStatus(value)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(`registrations.filters.status.${value}`)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DateRangeFilter
          from={search.from}
          to={search.to}
          onChange={(range) =>
            navigate({
              search: (prev) => ({ ...prev, from: range.from, to: range.to }),
              replace: true,
            })
          }
          disabled={isSearching}
        />
      </div>

      {counts ? (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button
            type="button"
            onClick={clearStatuses}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
              selectedStatuses.length === 0
                ? "border-primary bg-primary/10 font-medium text-foreground"
                : "border-input text-muted-foreground hover:bg-muted"
            }`}
          >
            {t("registrations.counts.total")}
            <span className="rounded-full bg-muted px-1.5 text-xs font-semibold text-foreground">
              {counts.total}
            </span>
          </button>
          {REGISTRATION_STATUSES.filter((s) => (counts.byStatus[s] ?? 0) > 0).map((s) => {
            const active = selectedStatuses.includes(s);
            const tone = REGISTRATION_TONE[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-all ${
                  TONE_CLASSES[tone]
                } ${
                  active
                    ? "font-medium ring-2 ring-current ring-offset-1"
                    : "hover:brightness-95"
                }`}
              >
                <span
                  className={`size-1.5 rounded-full ${toneDotClass(tone)}`}
                />
                {t(`registrations.filters.status.${s}`)}
                <span className="rounded-full bg-background/80 px-1.5 text-xs font-semibold text-foreground">
                  {counts.byStatus[s]}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <DataTable<Registration>
        data={data}
        columns={columns}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRowClick={(r) =>
          navigateTo({
            to: "/registrations/$registrationId",
            params: { registrationId: r.id },
          })
        }
        loadMore={
          !isSearching && listQuery.hasNextPage
            ? {
                hasMore: true,
                isLoading: listQuery.isFetchingNextPage,
                onLoadMore: () => listQuery.fetchNextPage(),
              }
            : undefined
        }
        emptyState={
          <EmptyState
            icon={FileSpreadsheet}
            title={
              isSearching
                ? t("registrations.empty.noResultsTitle")
                : status !== "all"
                  ? t("registrations.empty.noStatusTitle")
                  : t("registrations.empty.noneTitle")
            }
            description={
              isSearching
                ? t("registrations.empty.noResultsDescription", {
                    term: searchTerm,
                  })
                : status !== "all"
                  ? t("registrations.empty.noStatusDescription")
                  : t("registrations.empty.noneDescription")
            }
            actions={
              !isSearching ? (
                <Button onClick={() => setUploadOpen(true)}>
                  <Upload className="size-4" />
                  {t("registrations.actions.uploadLayout")}
                </Button>
              ) : null
            }
          />
        }
      />

      <UploadLayoutDialog open={uploadOpen} onOpenChange={setUploadOpen} />
      <ExportsDock open={exportsOpen} onOpenChange={setExportsOpen} />
      <LayoutUploadsDock open={uploadsHistoryOpen} onOpenChange={setUploadsHistoryOpen} />
    </>
  );
}
